import { NgClass, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';

import { ResourceItem } from '@ppg/core/enums';
import { ColorInfoValid } from '@ppg/core/models';
import { FeatureRoutePathService } from '@ppg/core/navigation';
import { ColorUtils } from '@ppg/shared/color/services';
import {
  ColorSwitchLinkCreator,
  STRIPE_ID_QUERY_PARAM,
  TintsAndShadesComponent,
  TintsAndShadesStripe,
} from '@ppg/shared/color/tints-and-shades';
import { FontColorBasedOnBackgroundDirective } from '@ppg/shared/components';
import { ResourceValuePipe } from '@ppg/shared/resource';

@Component({
  selector: 'ppg-color-details-header-banner',
  standalone: true,
  imports: [
    ButtonModule,
    FontColorBasedOnBackgroundDirective,
    TintsAndShadesComponent,
    ResourceValuePipe,
    NgClass,
    NgStyle,
  ],
  styleUrl: './color-details-header-banner.component.scss',
  templateUrl: './color-details-header-banner.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorDetailsHeaderBannerComponent {
  protected readonly PrimeIcons = PrimeIcons;

  readonly #colorUtils = inject(ColorUtils);
  readonly #featureRoutePathService = inject(FeatureRoutePathService);

  readonly colorSwitchLink = ((color, stripe) => ({
    routerLink: this.#featureRoutePathService.getColorDetailsPath(color),
    queryParams: { [STRIPE_ID_QUERY_PARAM]: stripe.id },
  })) satisfies ColorSwitchLinkCreator;

  readonly colorInfo = input.required<ColorInfoValid>();
  readonly isFavorite = input.required<boolean>();
  readonly tintsAndShades = input.required<TintsAndShadesStripe | null>();

  readonly toggleFavorite = output<void>();

  readonly #isFontLight = signal<boolean | undefined>(undefined);
  readonly isFontLight = this.#isFontLight.asReadonly();

  readonly tintsAndShadesHeading = ResourceItem.GLOBAL_TINTS_AND_SHADES;

  readonly hex = computed(() => {
    const transformedRgb = this.#colorUtils.stringToRgb(this.colorInfo().rgb);
    return this.#colorUtils.rgbToHex(transformedRgb);
  });

  onToggleFavoriteClick(): void {
    this.toggleFavorite.emit();
  }

  onFontShadeChange(isFontLight: boolean): void {
    this.#isFontLight.set(isFontLight);
  }
}
